/**
 * `workerOrigin()` — single source of truth for the Worker base URL.
 *
 * Issue #116 fixed the original hostname-sniffing helper:
 *   - It only redirected to `:8787` when `window.location.hostname` was
 *     exactly the string `"localhost"`. Playwright drives the harness
 *     at `127.0.0.1:5173`, so the check failed silently and every
 *     cross-origin fetch + WebSocket targeted Vite, which has no API.
 *
 * The replacement reads `VITE_WORKER_ORIGIN` at build time (Vite's
 * `import.meta.env` mechanism — the value is inlined at bundle time, so
 * production builds carry the right origin without any runtime probing
 * and there is no risk of accidentally falling back to a dev-only URL).
 *
 * Resolution order:
 *   1. `import.meta.env.VITE_WORKER_ORIGIN` — explicit override (the
 *      strongly-recommended path for both dev and prod). For dev, set
 *      this in `apps/client/.env.local`. For prod, set it on the build
 *      pipeline (Pages env var or CI step).
 *   2. Otherwise return the empty string — interpreted by the fetch
 *      calls as same-origin. This is correct for:
 *      - production (Pages app + Worker share a hostname)
 *      - local dev (Vite proxies `/auth` + `/v1` to the wrangler worker
 *        at :8787 — see `vite.config.ts`). Routing through the proxy
 *        keeps cookies same-site, which is the only way Safari's ITP
 *        will store the auth cookie over HTTP (Chrome has a localhost
 *        exception; Safari does not).
 *
 * For the WebSocket variant see `syncUrl()` in `app.ts` — it consumes
 * the same env var via a small wrapper so the wire-protocol scheme
 * (ws vs wss) is derived correctly. WS upgrades on `/v1/sync` are
 * forwarded by the same Vite proxy entry.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

interface HostLike {
  hostname?: string;
}

interface EnvLike {
  VITE_WORKER_ORIGIN?: string;
}

/**
 * Resolve the Worker origin string. Pure helper — accepts the
 * relevant subset of `window.location` and `import.meta.env` so the
 * caller can exercise the branches in a unit test without
 * monkey-patching globals.
 */
export function resolveWorkerOrigin(env: EnvLike, _loc: HostLike | null): string {
  const fromEnv = env.VITE_WORKER_ORIGIN;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    // Strip a trailing slash so callers can `${workerOrigin()}/v1/...`
    // without producing a double slash.
    return fromEnv.endsWith('/') ? fromEnv.slice(0, -1) : fromEnv;
  }
  // Same-origin — both for production (Pages + Worker on same hostname)
  // and for dev (Vite proxies `/auth` + `/v1` to wrangler at :8787; the
  // proxy is what keeps cookies same-site in Safari over HTTP).
  return '';
}

let warnedAboutMissingEnvInProd = false;

/**
 * The public entry point. Reads `import.meta.env` and `window.location`
 * once per call (cheap; both are present at module load on the client).
 *
 * Emits a one-shot console warning if a non-localhost build did not set
 * `VITE_WORKER_ORIGIN` — same-origin is the documented fallback for
 * Pages+Worker on the same hostname, but the warning makes the deploy
 * topology assumption visible rather than silently shrugging.
 */
export function workerOrigin(): string {
  const env = (import.meta as unknown as { env: EnvLike }).env;
  const loc: HostLike | null =
    typeof window === 'undefined' ? null : { hostname: window.location.hostname };
  const resolved = resolveWorkerOrigin(env, loc);

  if (
    resolved === '' &&
    !warnedAboutMissingEnvInProd &&
    loc !== null &&
    loc.hostname !== undefined &&
    !LOCAL_HOSTNAMES.has(loc.hostname)
  ) {
    warnedAboutMissingEnvInProd = true;
    console.warn(
      '[worker-origin] VITE_WORKER_ORIGIN not set — falling back to same-origin. ' +
        'This is correct only if the Pages app and the Worker share a hostname. ' +
        'Set VITE_WORKER_ORIGIN at build time to point at the deployed Worker.',
    );
  }
  return resolved;
}
